/**
 * A caixa de importação do extrato do Secullum.
 *
 * Papel: reproduz `#modal-overlay-bh-import` da origem — a área de arrastar,
 * o círculo e a mensagem enquanto o arquivo é lido, e a prévia com a tabela,
 * os selos "Novo", "Atualizar" e "Não encontrado", a contagem e o resumo — e
 * aplica pela `useImportarBancoHoras`.
 *
 * Como usar: quem monta põe a caixa só enquanto ela está aberta e passa a lista
 * inteira do banco de horas, com os meses, que é contra o que a prévia compara.
 * `onFechar` é chamado por "Cancelar" e depois de aplicar; a caixa não fecha por
 * `Esc` nem por clique fora.
 *
 * O arquivo nunca sai do navegador: o PDF é lido por `extrairItensDoPdf` e a
 * planilha por `lerCelulasDaPlanilha`, e as regras da origem de `secullum.ts`
 * decidem cada linha. A API só recebe `Previa.aEnviar`.
 *
 * As falhas saem em `alert()`, como na origem: "Erro ao importar: " com o
 * formato recusado ou o arquivo sem dado, e a mensagem fixa de leitura quando
 * a biblioteca não carregou ou o arquivo não é o que ela lê — a mensagem da
 * biblioteca não vai para a tela. Depois de aplicar, a caixa fecha e o
 * `alert()` de sucesso vem no tique seguinte, já com a caixa fora da tela, como
 * na origem.
 *
 * O negrito do resumo da prévia pesa 600 (`[&_strong]:font-semibold`): a origem
 * só carrega o Inter até 600 e desenha o `<strong>` com essa face; com o 700 de
 * verdade, cada contagem sairia mais larga e empurraria a seguinte.
 *
 * O rodapé não quebra linha (`flex-nowrap`), e os dois botões encolhem e quebram
 * o texto, como `.modal-actions` e `.btn`: em tela estreita, "✅ Aplicar N
 * registros" fica ao lado de "Cancelar", em duas linhas, em vez de descer.
 */
import { type ColaboradorBancoHoras } from '@concierge/shared';
import { type DragEvent, useRef, useState } from 'react';

import { ErroDaApi } from '@/shared/api';
import { formatarCompetencia } from '@/shared/lib/formato';
import { extrairItensDoPdf } from '@/shared/lib/pdf';
import { lerCelulasDaPlanilha } from '@/shared/lib/planilha';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { formatarAjuste, formatarHoras } from '../banco-horas';
import { useImportarBancoHoras } from '../hooks/usar-banco-horas';
import {
  type AcaoDaImportacao,
  type LinhaDoExtrato,
  type Previa,
  linhasDaPlanilha,
  linhasDoPdf,
  montarPrevia,
} from '../secullum';
import { handleAbrir, handleImpedirFechamento } from './caixa-da-origem';

/** Em que ponto a caixa está: escolhendo o arquivo, lendo, ou mostrando a prévia. */
type Fase =
  | { tipo: 'escolher' }
  | { tipo: 'lendo'; mensagem: string }
  | { tipo: 'previa'; previa: Previa };

/** Uma falha de leitura com a mensagem da origem, que pode ir ao `alert()`. */
class ErroDeLeitura extends Error {}

/** O selo de cada ação da prévia — `.bh-badge.new`, `.upd`, `.skip`. */
const SELO: Record<AcaoDaImportacao, { rotulo: string; classes: string }> = {
  novo: { rotulo: 'Novo', classes: 'bg-selo-concluido text-sucesso' },
  atualizar: { rotulo: 'Atualizar', classes: 'bg-brass/15 text-brass-light' },
  'nao-encontrado': { rotulo: 'Não encontrado', classes: 'bg-selo-vencido text-perigo' },
};

/** O que o `alert()` diz quando a biblioteca não carregou ou não leu o arquivo. */
const FALHA_DE_LEITURA =
  'Não foi possível ler o arquivo. Verifique a conexão com a internet e se o arquivo é o extrato do Secullum.';

/** O que o `alert()` diz numa falha ao aplicar que não veio da API. */
const FALHA_AO_APLICAR = 'Não foi possível aplicar a importação.';

/**
 * Escreve uma quantidade com o plural da origem.
 *
 * @param quantidade - O número.
 * @param palavra - A palavra no singular.
 * @returns `1 registro`, `2 registros`.
 */
function comPlural(quantidade: number, palavra: string): string {
  return `${quantidade} ${palavra}${quantidade > 1 ? 's' : ''}`;
}

interface ImportacaoSecullumProps {
  /** A lista inteira do banco de horas, com os meses. */
  colaboradores: readonly ColaboradorBancoHoras[];
  /** Chamado por "Cancelar" e depois de aplicar. */
  onFechar: () => void;
}

/**
 * A caixa da importação.
 *
 * @param colaboradores - A lista contra a qual a prévia casa os nomes e compara os meses.
 * @param onFechar - Chamado por "Cancelar" e depois de aplicar.
 * @returns O `Dialog` aberto de 720px.
 */
export function ImportacaoSecullum({ colaboradores, onFechar }: ImportacaoSecullumProps) {
  const [fase, setFase] = useState<Fase>({ tipo: 'escolher' });
  const [arrastando, setArrastando] = useState(false);
  const campoDoArquivo = useRef<HTMLInputElement>(null);
  const importar = useImportarBancoHoras();

  /**
   * Lê o arquivo pelo leitor da extensão, avisando a fase.
   *
   * @param arquivo - O arquivo escolhido ou solto.
   * @returns As linhas do extrato.
   */
  async function lerArquivo(arquivo: File): Promise<LinhaDoExtrato[]> {
    const nome = arquivo.name.toLowerCase();
    if (nome.endsWith('.pdf')) {
      setFase({ tipo: 'lendo', mensagem: 'Lendo PDF do Secullum...' });
      const paginas = await extrairItensDoPdf(arquivo, (pagina, total) =>
        setFase({ tipo: 'lendo', mensagem: `Lendo página ${pagina} de ${total}...` }),
      );
      return linhasDoPdf(paginas);
    }
    if (nome.endsWith('.xlsx') || nome.endsWith('.xls')) {
      setFase({ tipo: 'lendo', mensagem: 'Lendo planilha Excel...' });
      return linhasDaPlanilha(await lerCelulasDaPlanilha(arquivo));
    }
    throw new ErroDeLeitura('Formato não suportado. Use .pdf ou .xlsx');
  }

  /** Lê o arquivo e mostra a prévia, ou volta à área de arrastar com o `alert()`. */
  async function handleArquivo(arquivo: File) {
    setFase({ tipo: 'lendo', mensagem: 'Lendo arquivo...' });
    try {
      const linhas = await lerArquivo(arquivo);
      if (linhas.length === 0) throw new ErroDeLeitura('Nenhum dado encontrado no arquivo.');
      setFase({ tipo: 'previa', previa: montarPrevia(linhas, colaboradores) });
    } catch (erro) {
      setFase({ tipo: 'escolher' });
      window.alert(
        `Erro ao importar: ${erro instanceof ErroDeLeitura ? erro.message : FALHA_DE_LEITURA}`,
      );
    }
  }

  /** Lê o arquivo solto na área de arrastar. */
  function handleSoltar(evento: DragEvent) {
    evento.preventDefault();
    setArrastando(false);
    const arquivo = evento.dataTransfer.files[0];
    if (arquivo !== undefined) void handleArquivo(arquivo);
  }

  /** Grava o que a prévia mandou aplicar, fecha e avisa. */
  async function handleAplicar() {
    if (fase.tipo !== 'previa' || fase.previa.aEnviar.length === 0) return;
    const aplicados = fase.previa.novos + fase.previa.atualizar;
    try {
      await importar.mutateAsync({ linhas: fase.previa.aEnviar });
      onFechar();
      const aviso = `${comPlural(aplicados, 'registro')} aplicado${aplicados > 1 ? 's' : ''} com sucesso.`;
      window.setTimeout(() => window.alert(aviso), 0);
    } catch (erro) {
      window.alert(erro instanceof ErroDaApi ? erro.message : FALHA_AO_APLICAR);
    }
  }

  const previa = fase.tipo === 'previa' ? fase.previa : null;
  const aAplicar = previa === null ? 0 : previa.novos + previa.atualizar;

  return (
    <Dialog
      open
      onOpenChange={(aberta) => {
        if (!aberta) onFechar();
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        className="w-[720px]"
        onOpenAutoFocus={handleAbrir}
        onEscapeKeyDown={handleImpedirFechamento}
        onPointerDownOutside={handleImpedirFechamento}
        onInteractOutside={handleImpedirFechamento}
      >
        <DialogHeader>
          <DialogTitle>📥 Importar extrato do Secullum</DialogTitle>
        </DialogHeader>

        <input
          ref={campoDoArquivo}
          type="file"
          accept=".pdf,.xlsx,.xls"
          aria-label="Arquivo do extrato"
          className="hidden"
          onChange={(evento) => {
            const arquivo = evento.target.files?.[0];
            // Limpa o campo para que escolher o mesmo arquivo depois de uma falha
            // dispare o `onChange` de novo.
            evento.currentTarget.value = '';
            if (arquivo !== undefined) void handleArquivo(arquivo);
          }}
        />

        {fase.tipo === 'escolher' && (
          <button
            type="button"
            className={cn(
              'block w-full cursor-pointer rounded-md border-2 border-dashed border-linha-forte bg-superficie-2 p-8 text-center transition-colors hover:border-brass hover:bg-brass/6',
              arrastando && 'border-brass bg-brass/6',
            )}
            onClick={() => campoDoArquivo.current?.click()}
            onDragOver={(evento) => {
              evento.preventDefault();
              setArrastando(true);
            }}
            onDragLeave={() => setArrastando(false)}
            onDrop={handleSoltar}
          >
            <span className="mb-2 block text-[30px]">📄</span>
            <span className="mb-1 block text-sm/[1.6] font-semibold text-foreground">
              Arraste o arquivo aqui ou clique para selecionar
            </span>
            <span className="block text-xs/[1.6] text-texto-fraco">
              Aceita: extrato do Banco de Horas (.pdf) ou planilha Excel (.xlsx)
            </span>
          </button>
        )}

        {fase.tipo === 'lendo' && (
          <div className="p-[26px] text-center text-texto-medio">
            <div className="mb-2.5 inline-block size-7 animate-[spin_0.7s_linear_infinite] rounded-full border-[3px] border-linha-forte border-t-brass" />
            <div>{fase.mensagem}</div>
          </div>
        )}

        {previa !== null && (
          <div className="mt-3.5">
            <div className="mb-2.5 flex items-center gap-2 text-[13px] font-semibold">
              <span>✅ Dados encontrados</span>
              <span className="text-xs/[1.6] font-normal text-texto-medio">
                {previa.linhas.length > 0
                  ? `— ${comPlural(previa.linhas.length, 'registro')} com novidade`
                  : '— nenhuma novidade neste arquivo'}
              </span>
            </div>

            <div className="max-h-[340px] overflow-y-auto border border-linha-forte [&_[data-slot=table-container]]:border-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Colaborador</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead>Crédito</TableHead>
                    <TableHead>Débito</TableHead>
                    <TableHead>Ajuste</TableHead>
                    <TableHead>Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previa.linhas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="p-5 text-center text-texto-fraco">
                        Nenhum saldo novo ou diferente do que já está registrado.
                      </TableCell>
                    </TableRow>
                  ) : (
                    previa.linhas.map(({ linha, colaborador, acao }) => (
                      <TableRow
                        key={`${linha.nome}|${linha.competencia}`}
                        className={cn(acao === 'nao-encontrado' && 'opacity-50')}
                      >
                        <TableCell className="font-semibold">
                          {colaborador?.apelido ?? linha.nome}
                        </TableCell>
                        <TableCell>{formatarCompetencia(linha.competencia)}</TableCell>
                        <TableCell className="text-sucesso">
                          {formatarHoras(linha.creditoMin)}
                        </TableCell>
                        <TableCell className="text-perigo">
                          {formatarHoras(linha.debitoMin)}
                        </TableCell>
                        <TableCell>{formatarAjuste(linha.ajusteMin)}</TableCell>
                        <TableCell>
                          <span
                            className={cn(
                              'rounded-pill px-2 py-0.5 font-mono text-[10.5px] font-semibold whitespace-nowrap',
                              SELO[acao].classes,
                            )}
                          >
                            {SELO[acao].rotulo}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="mt-2.5 rounded-[4px] border border-linha bg-superficie-2 px-3 py-2.5 text-xs/[1.6] text-texto-medio [&_strong]:font-semibold">
              ✅ <strong>{previa.novos} novos</strong>
              {'   '}🔄 <strong>{previa.atualizar} para atualizar</strong>
              {previa.naoEncontrados > 0 && (
                <>
                  {'   '}⚠️ <strong>{previa.naoEncontrados} não encontrados</strong>
                </>
              )}
              {previa.semAlteracao > 0 && (
                <>
                  {'   '}· <strong>{previa.semAlteracao} sem alteração</strong> (mantidos como
                  estavam)
                </>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="flex-nowrap">
          <Button
            type="button"
            variant="outline"
            className="shrink text-center text-sm/[normal] whitespace-normal"
            onClick={onFechar}
          >
            Cancelar
          </Button>
          {aAplicar > 0 && (
            <Button
              type="button"
              className="shrink text-center text-sm/[normal] whitespace-normal"
              onClick={() => void handleAplicar()}
              disabled={importar.isPending}
            >
              ✅ Aplicar {comPlural(aAplicar, 'registro')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
